import { useState, useEffect, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { X, Plus, Linkedin, Twitter, Globe, Github, FileText, Video, Building } from "lucide-react";

interface UrlEntry {
  id: string;
  type: string;
  label: string;
  url: string;
}

interface DynamicUrlFieldProps {
  initialUrls?: Record<string, string>;
  onUrlsChange: (urls: Record<string, string>) => void;
}

const URL_TYPES = [
  { value: "linkedin", label: "LinkedIn", icon: Linkedin },
  { value: "twitter", label: "Twitter", icon: Twitter },
  { value: "github", label: "GitHub", icon: Github },
  { value: "website", label: "Website", icon: Globe },
  { value: "portfolio", label: "Portfolio", icon: FileText },
  { value: "blog", label: "Blog", icon: FileText },
  { value: "youtube", label: "YouTube", icon: Video },
  { value: "company", label: "Company", icon: Building },
  { value: "custom", label: "Custom", icon: Globe },
];

export const DynamicUrlField = ({ initialUrls = {}, onUrlsChange }: DynamicUrlFieldProps) => {
  const entryCounter = useRef(0);
  const isInitialized = useRef(false);
  
  const generateStableId = () => {
    entryCounter.current += 1;
    return `url-stable-${entryCounter.current}`;
  };

  // Initialize URL entries from initialUrls only once
  const [urlEntries, setUrlEntries] = useState<UrlEntry[]>(() => {
    const entries = Object.entries(initialUrls)
      .filter(([_, url]) => url && url.trim() !== "")
      .map(([key, url]) => ({
        id: generateStableId(),
        type: key,
        label: key === "custom" ? key : "",
        url: url as string,
      }));
    
    isInitialized.current = true;
    return entries;
  });

  const addUrlEntry = () => {
    const newEntry: UrlEntry = {
      id: generateStableId(),
      type: "linkedin",
      label: "",
      url: "",
    };
    setUrlEntries(prev => {
      const updated = [...prev, newEntry];
      updateFormValues(updated);
      return updated;
    });
  };

  const removeUrlEntry = (id: string) => {
    const updatedEntries = urlEntries.filter((entry) => entry.id !== id);
    setUrlEntries(updatedEntries);
    updateFormValues(updatedEntries);
  };

  const updateUrlEntry = (id: string, field: keyof UrlEntry, value: string) => {
    setUrlEntries(prev => {
      const updatedEntries = prev.map((entry) =>
        entry.id === id ? { ...entry, [field]: value } : entry
      );
      updateFormValues(updatedEntries);
      return updatedEntries;
    });
  };

  const updateFormValues = (entries: UrlEntry[]) => {
    const urlsObject: Record<string, string> = {};
    entries.forEach((entry) => {
      if (entry.url.trim()) {
        const key = entry.type === "custom" && entry.label ? entry.label : entry.type;
        urlsObject[key] = entry.url;
      }
    });
    onUrlsChange(urlsObject);
  };

  const getIcon = (type: string) => {
    const urlType = URL_TYPES.find(t => t.value === type);
    const IconComponent = urlType?.icon || Globe;
    return <IconComponent className="h-4 w-4" />;
  };

  return (
    <div className="space-y-3">
      {urlEntries.map((entry) => (
        <div key={entry.id} className="flex items-center space-x-2">
          <div className="w-32">
            <Select
              value={entry.type}
              onValueChange={(value) => updateUrlEntry(entry.id, "type", value)}
            >
              <SelectTrigger className="h-9">
                <div className="flex items-center space-x-2">
                  {getIcon(entry.type)}
                  <span>{URL_TYPES.find(t => t.value === entry.type)?.label || entry.type}</span>
                </div>
              </SelectTrigger>
              <SelectContent>
                {URL_TYPES.map((type) => (
                  <SelectItem key={type.value} value={type.value}>
                    <div className="flex items-center space-x-2">
                      <type.icon className="h-4 w-4" />
                      <span>{type.label}</span>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          {entry.type === "custom" && (
            <div className="w-24">
              <Input
                placeholder="Label"
                value={entry.label}
                onChange={(e) => updateUrlEntry(entry.id, "label", e.target.value)}
                className="h-9"
              />
            </div>
          )}
          
          <div className="flex-1">
            <Input
              type="text"
              placeholder="Enter any value"
              value={entry.url}
              onChange={(e) => updateUrlEntry(entry.id, "url", e.target.value)}
              className="h-9"
            />
          </div>
          
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => removeUrlEntry(entry.id)}
            className="h-9 w-9 p-0 text-muted-foreground hover:text-destructive"
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      ))}

      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={addUrlEntry}
        className="w-full"
      >
        <Plus className="h-4 w-4 mr-2" />
        Add URL
      </Button>
    </div>
  );
};

export default DynamicUrlField;